import React, { useEffect, useState } from 'react';
import { FaExclamationTriangle, FaMapMarkerAlt, FaClock, FaInfoCircle, FaRegCalendarAlt } from 'react-icons/fa';
import { fetchProgram } from '../config';
import './ProgramPage.css';

// Session-cell colour coding for the master timetable (tint + accent + label).
const CELL_TYPES = {
  plenary: { label: 'Plenary' },
  keynote: { label: 'Keynote' },
  'mini-symposium': { label: 'Mini-Symposium' },
  special: { label: 'Special Session' },
  parallel: { label: 'Parallel Session' },
  event: { label: 'Event' },
};

// Render "\n" inside plain-text content as line breaks.
const renderMultiline = (text) => {
  const lines = String(text ?? '').split('\n');
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {index > 0 && <br />}
      {line}
    </React.Fragment>
  ));
};

const TentativeNotice = ({ notice, noticeDetail }) => {
  if (!notice && !noticeDetail) return null;
  return (
    <div className="program-notice" role="note">
      <div className="program-notice-icon">
        <FaExclamationTriangle />
      </div>
      <div className="program-notice-text">
        {notice && <div className="program-notice-title">{notice}</div>}
        {noticeDetail && <p className="program-notice-detail">{renderMultiline(noticeDetail)}</p>}
      </div>
    </div>
  );
};

// A grouped parallel cell (linked to a presentation group server-side) has its
// group data attached as { number, title }; returns it only when present.
const groupOf = (cell) => {
  const g = cell && cell.presentationGroup && typeof cell.presentationGroup === 'object' ? cell.presentationGroup : null;
  return (CELL_TYPES[cell.type] ? cell.type : 'event') === 'parallel' && g && g.number != null ? g : null;
};

// The colour/accent class for one (simple) session cell.
const cellClass = (cell) => {
  const type = CELL_TYPES[cell.type] ? cell.type : 'event';
  return groupOf(cell) ? 'program-cell--parallel program-cell--parallel-grouped' : `program-cell--${type}`;
};

// The inner content of one (simple) session cell: type label + title + details.
// A grouped parallel cell shows "Parallel Session — Group N" + the group title.
const CellInner = ({ cell }) => {
  const type = CELL_TYPES[cell.type] ? cell.type : 'event';
  const group = groupOf(cell);
  return (
    <>
      <span className="program-cell-type">{CELL_TYPES[type].label}</span>
      <span className="program-cell-title">{group ? `Parallel Session — Group ${group.number}` : cell.title}</span>
      {group && group.title ? <span className="program-cell-group-title">{group.title}</span> : null}
      {cell.organisers ? <span className="program-cell-detail">{cell.organisers}</span> : null}
      {cell.detail ? <span className="program-cell-detail">{cell.detail}</span> : null}
    </>
  );
};

const TimetableCell = ({ cell }) => {
  if (!cell || typeof cell !== 'object') {
    return (
      <td className="program-cell program-cell-empty">
        <span aria-hidden="true">&mdash;</span>
      </td>
    );
  }
  // A shared cell holds two sessions in one room + time slot: stack them, each
  // keeping its own colour and label. No "shared" wording — just the two cells.
  if (cell.type === 'shared') {
    const subs = (Array.isArray(cell.sessions) ? cell.sessions : []).filter((s) => s && typeof s === 'object');
    if (subs.length === 0) {
      return (
        <td className="program-cell program-cell-empty">
          <span aria-hidden="true">&mdash;</span>
        </td>
      );
    }
    return (
      <td className="program-cell program-cell--shared">
        <div className="program-shared-stack">
          {subs.map((sub, i) => (
            <div className={`program-shared-item ${cellClass(sub)}`} key={i}>
              <CellInner cell={sub} />
            </div>
          ))}
        </div>
      </td>
    );
  }
  return (
    <td className={`program-cell ${cellClass(cell)}`}>
      <CellInner cell={cell} />
    </td>
  );
};

const TimetableDay = ({ day, index }) => {
  const columns = Array.isArray(day.columns) ? day.columns : [];
  const rows = Array.isArray(day.rows) ? day.rows : [];
  if (columns.length === 0 && rows.length === 0) return null;

  return (
    <div className="program-table-card" style={{ animationDelay: `${0.1 + Math.min(index, 10) * 0.05}s` }}>
      <h3 className="program-table-title">{day.title}</h3>
      <div className="table-wrapper">
        <table className="program-table">
          <thead>
            <tr>
              <th scope="col" className="program-th-time">Session / Time</th>
              {columns.map((room, index) => (
                <th scope="col" key={index}>{room}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              if (!row || typeof row !== 'object') return null;
              const rowHead = (
                <th scope="row" className="program-row-head">
                  {row.slot ? <span className="program-row-slot">{row.slot}</span> : null}
                  {row.time ? <span className="program-row-time">{row.time}</span> : null}
                </th>
              );
              // A row has span XOR cells — span wins if both are present.
              if (row.span != null) {
                return (
                  <tr key={rowIndex} className="program-break-row">
                    {rowHead}
                    <td colSpan={Math.max(columns.length, 1)} className="program-break-cell">
                      {row.span}
                    </td>
                  </tr>
                );
              }
              const cells = Array.isArray(row.cells) ? row.cells : [];
              return (
                <tr key={rowIndex}>
                  {rowHead}
                  {columns.map((_, colIndex) => (
                    <TimetableCell key={colIndex} cell={cells[colIndex]} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ProgramPage = () => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fetched = await fetchProgram();
      if (cancelled) return;
      if (fetched) setProgram(fetched);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const intro = program?.intro || {};
  const venueParagraphs = Array.isArray(intro.venueParagraphs) ? intro.venueParagraphs : [];
  const days = Array.isArray(program?.days) ? program.days : [];
  const timetable = Array.isArray(program?.timetable) ? program.timetable : [];

  return (
    <section className="program-page fade-in-up">
      <div className="program-wrapper">
        {/* Hero + schedule descriptions (constrained to 900px) */}
        <div className="program-inner">
          <span className="badge program-badge">Information</span>
          <h1 className="program-heading">Conference Program</h1>
          <p className="program-subtitle">
            An overview of the ICES2026 conference schedule, day by day, followed by the
            delegate master timetable.
          </p>

          {loading && (
            <div className="program-loading">
              <div className="program-spinner" />
              <p>Loading the conference program&hellip;</p>
            </div>
          )}

          {!loading && !program && (
            <div className="program-empty">
              <FaRegCalendarAlt className="program-empty-icon" />
              <p>The conference program isn&rsquo;t available just yet. Please check back later.</p>
            </div>
          )}

          {/* Prominent tentative-program notice */}
          <TentativeNotice notice={program?.notice} noticeDetail={program?.noticeDetail} />

          {/* Schedule at a glance */}
          {intro.heading && <h2 className="program-section-heading">{intro.heading}</h2>}

          {(intro.venueHeading || venueParagraphs.length > 0) && (
            <div className="program-venue-card">
              <div className="program-venue-header">
                <div className="program-venue-icon">
                  <FaMapMarkerAlt />
                </div>
                {intro.venueHeading && <h3 className="program-venue-heading">{intro.venueHeading}</h3>}
              </div>
              {venueParagraphs.map((paragraph, index) => (
                <p className="program-venue-paragraph" key={index}>
                  {renderMultiline(paragraph)}
                </p>
              ))}
            </div>
          )}

          {days.map((day, dayIndex) => {
            if (!day || typeof day !== 'object') return null;
            const blocks = Array.isArray(day.blocks) ? day.blocks : [];
            const keyTimes = Array.isArray(day.keyTimes) ? day.keyTimes : [];
            return (
              <div className="program-day-card" key={day.id || dayIndex} style={{ animationDelay: `${0.1 + Math.min(dayIndex, 10) * 0.05}s` }}>
                <h2 className="program-day-title">{day.title}</h2>
                {blocks.map((block, blockIndex) => {
                  if (!block || typeof block !== 'object') return null;
                  return (
                    <div className="program-day-block" key={blockIndex}>
                      {block.heading ? (
                        <h4 className="program-block-heading">{block.heading}</h4>
                      ) : null}
                      {block.text ? (
                        <p className="program-block-text">{renderMultiline(block.text)}</p>
                      ) : null}
                    </div>
                  );
                })}
                {keyTimes.length > 0 && (
                  <div className="program-key-times">
                    <div className="program-key-times-heading">
                      <FaClock className="program-key-times-icon" /> Key Times
                    </div>
                    {keyTimes.map((item, timeIndex) => {
                      if (!item || typeof item !== 'object') return null;
                      return (
                        <div className="program-key-time-row" key={timeIndex}>
                          <div className="program-key-time">{item.time}</div>
                          <div className="program-key-label">{item.label}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Slim tentative reminder before the timetable */}
          {program?.notice && (
            <div className="program-reminder" role="note">
              <FaExclamationTriangle className="program-reminder-icon" />
              <span>
                <strong>{program.notice}</strong>
                {program.noticeDetail ? <> &mdash; {renderMultiline(program.noticeDetail)}</> : null}
              </span>
            </div>
          )}
        </div>

        {/* Master timetable (full 1100px width) */}
        {timetable.length > 0 && (
          <div className="program-timetable-section">
            <h2 className="program-section-heading program-timetable-heading">Master Timetable</h2>
            <div className="program-legend" aria-label="Session type legend">
              {Object.keys(CELL_TYPES).map((type) => (
                <span className={`program-legend-chip program-legend-chip--${type}`} key={type}>
                  <span className="program-legend-swatch" aria-hidden="true" />
                  {CELL_TYPES[type].label}
                </span>
              ))}
            </div>
            {timetable.map((day, index) =>
              day && typeof day === 'object' ? <TimetableDay day={day} index={index} key={day.id || index} /> : null
            )}
          </div>
        )}

        {/* Outro note */}
        {program?.outro && (
          <div className="program-outro">
            <div className="program-outro-icon">
              <FaInfoCircle />
            </div>
            <p className="program-outro-text">{renderMultiline(program.outro)}</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProgramPage;
